/**
 * useScopingSnapshot (017) — read path for the V2 scoping captured-structure
 * rail (HYBRID).
 *
 * `GET /api/v1/projects/:projectId/briefing-context?mode=scoping` returns both:
 *   - `data.project` — the project entity (decision_intent + snapshot.bets /
 *     learning_goals): the rail's BASE, since the live scoping conversation does
 *     not (yet) write `briefing_draft`.
 *   - `data.snapshot` — the scoping conversation's `resume_memory.snapshot`: the
 *     live `briefing_draft` OVERLAY when/if the pipeline starts writing it.
 *
 * `?mode=scoping` forces the conversation lookup to the scoping session (the
 * endpoint otherwise derives the conversation from project status, which returns
 * the *update* conversation for a non-draft project).
 *
 * Re-reads when `refreshKey` (the conversation turn count) changes so the rail
 * reflects the latest captured structure after each turn (FR-008);
 * `keepPreviousData` avoids a flicker to the empty rail during the swap. The
 * mapping into rail rows is `buildScopingRailRowsFromContext` (pure, tested);
 * this hook only owns the fetch.
 */

import { useQuery, keepPreviousData, type Query } from '@tanstack/react-query';

import {
  isScopeLocked,
  type ScopingProjectInput,
  type ScopingSnapshotInput,
} from '@/components/v2/conversation/scoping/scopingRailModel';

export interface UseScopingSnapshotArgs {
  projectId: string | null | undefined;
  /**
   * Bump to force a re-read. The scoping page passes the conversation turn
   * count so the rail refreshes after each turn.
   */
  refreshKey?: number;
  /**
   * Enable the backstop poll while a scoping call is ACTIVE. Pass `false` when
   * idle/ended so the rail does not poll outside a live call.
   *
   * NOTE — this is a *backstop*, not the primary sync mechanism. The rail's
   * primary, event-driven trigger is `refreshKey` (the client turn count): it
   * advances once per turn on BOTH transports — text turns natively, and voice
   * turns since the agent bridges each spoken turn's transcript back through the
   * LiveKit data channel into the same `turns` array (the Fix B transcript
   * bridge, which post-dates the original Fix A poll). A turn change re-keys the
   * query and refetches immediately, so the rail tracks captures per turn without
   * polling. This interval only reconciles two edge cases the per-turn refetch
   * can miss: a dropped data-channel message, and the snapshot-persist race (a
   * transcript event can arrive just before Cloud commits that turn's snapshot,
   * so the immediate refetch reads the pre-turn snapshot). Because the query key
   * includes `refreshKey`, each turn resets this interval — it therefore only
   * fires during a quiet gap with no new turn, exactly when a backstop is useful.
   */
  pollWhileActive?: boolean;
}

/**
 * Backstop poll cadence for the scoping rail while a call is active. The primary
 * per-turn refetch is `refreshKey`-driven (see `pollWhileActive`); this only
 * covers dropped transcript messages + the snapshot-persist race, so it is a slow
 * safety net (15s) rather than the aggressive 4s poll it replaced — cutting the
 * mid-call briefing-context request volume ~4x. Each poll is a conditional GET
 * (ETag → 304 when unchanged), but a 304 still costs full server work (the route
 * re-runs its query set before the ETag is compared), so fewer polls is the win.
 */
export const SCOPING_RAIL_POLL_MS = 15000;

/**
 * react-query `refetchInterval` value for the rail: backstop-poll while a call is
 * active, never when idle/ended (avoids polling a static page indefinitely).
 */
export function scopingRailPollInterval(active: boolean): number | false {
  return active ? SCOPING_RAIL_POLL_MS : false;
}

export interface UseScopingSnapshotResult {
  /** Project entity — the rail's base (decision_intent + snapshot bets/goals). */
  project: ScopingProjectInput | null;
  /** Live scoping conversation snapshot — `briefing_draft` overlay when present. */
  snapshot: ScopingSnapshotInput | null;
  /**
   * The existing scoping conversation_id for this project (from conversation_mappings).
   * Non-null when there is a resumable session (see `isResumable`).
   */
  conversationId: string | null;
  /**
   * True when there is an existing, non-expired scoping session with persisted
   * state that can be resumed. Used by the scoping page to decide whether to
   * reconnect vs. start fresh.
   */
  isResumable: boolean;
  /**
   * True when the existing scoping session has persisted state but its TTL has
   * lapsed (`expires_at < now`). Its transcript + snapshot are still durable in
   * Postgres, so it can be resumed on an EXPLICIT re-entry (the orient-card
   * voice/text click) even though it is not auto-resumable. Mutually exclusive
   * with `isResumable`.
   */
  isExpired: boolean;
  isLoading: boolean;
  /** True when the briefing-context fetch failed (non-retried error). */
  isError: boolean;
}

interface BriefingContextConversation {
  id: string;
  found: true;
  expired?: boolean;
  hasResumeMemory: boolean;
}

interface BriefingContext {
  project: ScopingProjectInput | null;
  snapshot: ScopingSnapshotInput | null;
  conversationId: string | null;
  isResumable: boolean;
  isExpired: boolean;
}

/**
 * Exported for unit testing (ANA-227).
 *
 * Returns true when a failed briefing-context fetch should be retried:
 *   - 401 = auth-init race (cookie refresh settling)
 *   - 5xx = transient server error
 *   - no status = network failure (fetch threw before a response arrived)
 * Any other 4xx (404, 400, 403, ...) surfaces immediately -- they are not
 * transient and retrying them would only delay the error state.
 */
export function shouldRetry(failureCount: number, error: Error): boolean {
  if (failureCount >= 2) return false;
  const status = (error as Error & { status?: number })?.status;
  // 401 = auth-init race (cookie refresh settling)
  // 5xx or no status = transient server/network error
  return status === 401 || !status || (status >= 500 && status < 600);
}

export function useScopingSnapshot({
  projectId,
  refreshKey = 0,
  pollWhileActive = false,
}: UseScopingSnapshotArgs): UseScopingSnapshotResult {
  const query = useQuery<BriefingContext, Error>({
    // refreshKey participates in the key so a turn completing triggers a fresh
    // read; keepPreviousData avoids a flicker to the empty rail during the swap.
    queryKey: ['v2', 'scopingSnapshot', projectId, refreshKey] as const,
    enabled: Boolean(projectId),
    staleTime: 0,
    refetchOnWindowFocus: false,
    // Backstop poll while a call is active (the primary per-turn refetch is
    // `refreshKey`-driven — see the `pollWhileActive` doc). Off (false) when
    // idle/ended. Not in background (tab hidden ⇒ rail not visible).
    //
    // Also stop once the fetched project is locked (bug report point 4): a
    // locked scope is already promoted into the graph, so briefing-context has
    // nothing further to teach the rail. `callActive` alone doesn't cover this
    // — the call intentionally STAYS active post-lock for Q&A/goodbye (see
    // ProjectScopingPage's `showLockedState` gate), so gating polling on the
    // query's own last-fetched data (rather than `pollWhileActive`/`callActive`)
    // is what actually stops it without disturbing that UI behaviour.
    refetchInterval: (query: Query<BriefingContext, Error>) =>
      pollWhileActive && !isScopeLocked(query.state.data?.project?.status)
        ? scopingRailPollInterval(true)
        : false,
    placeholderData: keepPreviousData,
    retry: shouldRetry,
    retryDelay: (attempt) => Math.min(300 * 2 ** attempt, 2000),
    queryFn: async () => {
      const res = await fetch(
        `/api/v1/projects/${encodeURIComponent(projectId!)}/briefing-context?mode=scoping`,
        { credentials: 'include' },
      );
      if (!res.ok) {
        const err = new Error(`Briefing context fetch failed: ${res.status}`) as Error & {
          status?: number;
        };
        err.status = res.status;
        throw err;
      }
      const body = (await res.json()) as
        | {
            data?: {
              project?: ScopingProjectInput | null;
              snapshot?: ScopingSnapshotInput | null;
              conversation?: BriefingContextConversation;
            };
          }
        | null;
      const conv = body?.data?.conversation;
      const resumable = Boolean(conv && conv.found && conv.hasResumeMemory);
      return {
        project: body?.data?.project ?? null,
        snapshot: body?.data?.snapshot ?? null,
        conversationId: conv?.id ?? null,
        // Auto-resumable only when not expired; an expired-but-resumable session
        // is surfaced separately (isExpired) for explicit re-entry resume.
        isResumable: resumable && !conv?.expired,
        isExpired: resumable && Boolean(conv?.expired),
      };
    },
  });

  return {
    project: query.data?.project ?? null,
    snapshot: query.data?.snapshot ?? null,
    conversationId: query.data?.conversationId ?? null,
    isResumable: query.data?.isResumable ?? false,
    isExpired: query.data?.isExpired ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
